import { Badge } from "@/components/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/table";
import { EvidenceGateRollout, EvidenceGatesCheck1Response } from "@/http/api-types";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { byRolloutDesc, dailySpend, formatEUR, formatLift, inclusiveDays, liftToneClass } from "./check-1";

// Week 2 is the gate figure and week 1 is context, fixed in advance so neither can be
// chosen after the fact. Both are shown because the gap between them is the signal
// that the system was still settling.
function LiftPair({ week1, week2, threshold }: { week1: number | null; week2: number | null; threshold: number }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className={cn("font-semibold tabular-nums", liftToneClass(week2, threshold))}>{formatLift(week2)}</span>
      <span className="text-xs tabular-nums text-muted-foreground">wk1 {formatLift(week1)}</span>
    </div>
  );
}

function RolloutRow({ rollout, threshold }: { rollout: EvidenceGateRollout; threshold: number }) {
  const baselineDays = inclusiveDays(rollout.start_date, rollout.end_date);

  return (
    <TableRow className={cn("group/row hover:bg-muted", !rollout.measurable && "text-muted-foreground")}>
      <TableCell className="tabular-nums">
        <Link to={`/experiments/${rollout.experiment_id}`} className="hover:underline">
          {rollout.experiment_id}
        </Link>
      </TableCell>
      <TableCell className="max-w-[20rem] truncate" title={rollout.name}>
        <Link to={`/experiments/${rollout.experiment_id}`} className="block truncate hover:underline">
          {rollout.name}
        </Link>
      </TableCell>
      <TableCell className="tabular-nums">{rollout.rollout_at.slice(0, 10)}</TableCell>
      <TableCell>
        {rollout.is_win ? <Badge variant="violet">win</Badge> : <span className="text-xs">no</span>}
      </TableCell>
      <TableCell>
        <LiftPair week1={rollout.week1_install_lift} week2={rollout.week2_install_lift} threshold={threshold} />
      </TableCell>
      <TableCell>
        <LiftPair week1={rollout.week1_action_lift} week2={rollout.week2_action_lift} threshold={threshold} />
      </TableCell>
      {/* Daily spend, not totals: the baseline spans the whole experiment and the post
          window seven days, so only the rates are comparable. */}
      <TableCell className="text-right tabular-nums">
        {formatEUR(dailySpend(rollout.baseline_spend_eur, baselineDays))} →{" "}
        {formatEUR(dailySpend(rollout.week2_spend_eur, 7))}
      </TableCell>
      <TableCell className="text-right tabular-nums">{rollout.week2_installs.toLocaleString("en-US")}</TableCell>
      <TableCell className="whitespace-nowrap">
        {rollout.durability_proven ? (
          <span className="text-muted-foreground">passed</span>
        ) : (
          <Badge variant="yellow">by {rollout.durable_at.slice(0, 10)}</Badge>
        )}
      </TableCell>
      <TableCell>
        {rollout.overlapped_by.length > 0 ? (
          <Badge variant="warn">{rollout.overlapped_by.join(", ")}</Badge>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
    </TableRow>
  );
}

export function Check1Table({ data }: { data: EvidenceGatesCheck1Response }) {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>ID</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Rolled out</TableHead>
            <TableHead>Win</TableHead>
            <TableHead>Installs / €</TableHead>
            <TableHead>Actions / €</TableHead>
            <TableHead className="text-right">Spend/day base → wk2</TableHead>
            <TableHead className="text-right">Wk2 installs</TableHead>
            <TableHead>90 days</TableHead>
            <TableHead>Overlaps</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {byRolloutDesc(data.rollouts).map(rollout => (
            <RolloutRow key={rollout.experiment_id} rollout={rollout} threshold={data.lift_threshold} />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
